import styled, { keyframes } from 'styled-components';

export const Container = styled.section`
  background-color: #ffffff;
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  position: relative;
`;

export const MainHeading = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 3rem;
  color: #2D3436;
  max-width: 1100px;
  text-align: center;
  margin-bottom: 4rem;
  line-height: 1.2;
  font-weight: 500;
  @media (max-width: 768px) {
    font-size: 2rem;
    padding: 0 1rem;
    margin-bottom: 2rem;
  }
`;

export const FeaturesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4rem;
  margin-bottom: 4rem;
  width: 100%;
  max-width: 1200px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    max-width: 600px;
  }
`;


export const FeatureCard = styled.div`
  background: #FFFFFF;
  border: 2px solid #E9F7FC;
  border-radius: 24px;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(11, 213, 241, 0.1);
  }
`;

export const FeatureTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  color: #2D3436;
  margin-bottom: 1.2rem;
  font-weight: 700;
  text-align: center;
`;

export const FeatureDescription = styled.p`
  font-size: 1.1rem;
  color: #636E72;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 2.5rem;
  min-height: 80px;
  padding: 0 1rem;
`;

export const LearnMoreButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #2563EB;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  &:hover {
    background: rgba(11, 213, 241, 0.1);
  }
`;

export const Arrow = styled.span`
  margin-left: 0.6rem;
  transition: transform 0.3s ease;
  ${LearnMoreButton}:hover & {
    transform: translateX(5px);
  }
`;

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(calc(-50% - 16px)); }
`;


export const IconImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  display: block;
  margin: 0;
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
`;

export const IconLines = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
  }
`;

export const FeatureIcon = styled.div`
  margin-bottom: 2.5rem;
  background-color: #E9F7FC;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }
`;

export const ExpandedContent = styled.div`
  grid-column: 1 / -1;
  background: #F8FDFF;
  border: 2px solid #E9F7FC;
  border-radius: 16px;
  padding: 2rem;
  margin-top: -2rem;
  margin-bottom: 2rem;
  animation: fadeIn 0.3s ease-in-out;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.1);
  position: relative;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 1024px) {
    margin-top: -1rem;
  }
`;

export const ExpandedTitle = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-size: 1.8rem;
  color: #2563EB;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

export const ExpandedText = styled.p`
  font-size: 1.1rem;
  color: #2D3436;
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

export const CloseButton = styled.button`
  background: none;
  border: 2px solid #2563EB;
  color: #2563EB;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: #2563EB;
    color: white;
  }
`;

export const WordSliderContainer = styled.div`
  overflow: hidden;
  width: 100%;
  margin: 1.5rem 0 2.5rem;
  position: relative;
  max-width: 1200px;
  
  &::before, &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 15%;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, white 0%, rgba(255, 255, 255, 0) 100%);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, white 0%, rgba(255, 255, 255, 0) 100%);
  }
`;

export const WordSliderTrack = styled.ul`
  display: flex;
  width: max-content;
  list-style-type: none;
  padding: 0;
  margin: 0;
  gap: 32px;
  animation: ${scroll} 30s linear infinite;
  
  @media (max-width: 768px) {
    animation: ${scroll} 23s linear infinite;
  }
  
  @media (max-width: 480px) {
    animation: ${scroll} 20s linear infinite;
  }
`;



export const WordItem = styled.li`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
`;

export const PrefixText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 40px;
  color: #474747;
  margin: 0;
`;

export const MainWord = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 40px;
  font-weight: 600;
  color: ${props => props.color};
  margin: 0;
`;
